import { toUniversalDateString, utcToLocalDate } from '@assetaccountant/core';
import { AaCurrencyPipe } from '@assetaccountant/core/pipes/aa-currency.pipe';
import { AaDatePipe } from '@assetaccountant/core/pipes/aa-date.pipe';
import { AaPercentPipe } from '@assetaccountant/core/pipes/aa-percent.pipe';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import {
  getIntegrationName,
  IntegrationType,
} from '@assetaccountant/integrations/models/integration-type';

import { AccountingYear } from '@assetaccountant/api/models/accounting-year';
import { AssetGroupDto } from '@assetaccountant/api/models/asset-group.dto';
import { AssetTransactionDto } from '@assetaccountant/api/models/asset-transaction.dto';
import { AssetApi } from '@assetaccountant/api/services/asset.api';
import { PickNonNullable } from '@assetaccountant/core/utils/filter-nulls';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ALL_TYPES_BUT_REVERSALS, AssetTransaction } from './asset-transaction-api-model';

function getDateCondition(date: string): {
  isUntilInclusive(transaction: AssetTransactionDto): boolean;
  isExactly(transaction: AssetTransactionDto): boolean;
} {
  date = toUniversalDateString(date, { throwOnFailure: true });

  return {
    isUntilInclusive(transaction) {
      return toUniversalDateString(transaction.date, { throwOnFailure: true }) <= date;
    },
    isExactly(transaction) {
      return toUniversalDateString(transaction.date, { throwOnFailure: true }) === date;
    },
  };
}

export interface AssetState {
  cost: number | null;
  writtenDownValue: number | null;
  nextWrittenDownValue: number | null;
  originalCost: number | null;
  residualValue: number;
  impairmentAdjustment: number | null;
  impairmentProvision: number | null;
  unimpairedRate: number;
  unimpairedValue: number | null;
  unrevaluedValue: number | null;
}

export interface AssetTransactionAdjusted extends AssetTransactionDto {
  financialYear: string | null;
  index: number;
  showResidualValue?: boolean;
  isPriorToOpeningBalance?: boolean;
  openingBalanceLabel?: string;
  warnings?: Array<{
    text: string[];
    action?: 'REASSESS';
  }>;
}

export function getAssetStateUntilDateInclusive(
  transactions: AssetTransactionDto[],
  date: string | null | undefined,
): AssetState {
  const result: AssetState = {
    cost: null,
    writtenDownValue: null,
    nextWrittenDownValue: null,
    originalCost: null,
    residualValue: 0,
    impairmentAdjustment: null,
    impairmentProvision: null,
    unimpairedRate: 1,
    unimpairedValue: null,
    unrevaluedValue: null,
  };

  if (date) {
    const dateCondition = getDateCondition(date);

    let hasExactWrittenDownValue = false;
    for (const transaction of transactions) {
      if (dateCondition.isUntilInclusive(transaction)) {
        if (transaction.cost != null) {
          result.cost = transaction.cost + (result.cost || 0);
        }
        if (transaction.writtenDownValue != null) {
          result.writtenDownValue = transaction.writtenDownValue;
          if (dateCondition.isExactly(transaction)) {
            hasExactWrittenDownValue = true;
          }

          result.unimpairedValue = transaction.unimpairedValue ?? transaction.writtenDownValue;
          result.unrevaluedValue = transaction.unrevaluedValue ?? transaction.writtenDownValue;
        }
        if (transaction.originalCost != null) {
          result.originalCost = transaction.originalCost;
        }
        if (transaction.residualValue != null) {
          result.residualValue = transaction.residualValue;
        }
        if (transaction.impairmentAdjustment != null) {
          result.impairmentAdjustment =
            (result.impairmentAdjustment || 0) + transaction.impairmentAdjustment;
        }
        if (transaction.impairmentProvision != null) {
          result.impairmentProvision = transaction.impairmentProvision;
        }
        if (transaction.unimpairedRate != null) {
          result.unimpairedRate = transaction.unimpairedRate;
        }
      } else if (!hasExactWrittenDownValue) {
        if (transaction.writtenDownValue != null) {
          result.nextWrittenDownValue = transaction.writtenDownValue;
          break;
        }
      } else {
        break;
      }
    }
  }

  return result;
}

export function getAccurateAssetStateUntilDateInclusive(
  assetApi: AssetApi,
  transactions: AssetTransaction[] | AssetTransactionDto[],
  {
    assetId,
    regimeId,
    date,
    isEndOfDay,
  }: {
    assetId: number;
    regimeId: number;
    date?: string | null;
    isEndOfDay: boolean;
  },
): Observable<AssetState> {
  const assetState = getAssetStateUntilDateInclusive(transactions, date);

  if (
    !stateHasWrittenDownValue(assetState) ||
    (assetState.nextWrittenDownValue == null && isEndOfDay) ||
    assetState.writtenDownValue === assetState.nextWrittenDownValue
  ) {
    return of(assetState);
  }

  return assetApi
    .getValue({
      assetId,
      asOf: date,
      isEndOfDay,
    })
    .pipe(
      map(({ valuesByRegimeId }) => ({
        ...assetState,
        writtenDownValue: valuesByRegimeId[regimeId] ?? assetState.writtenDownValue,
      })),
      catchError(() => of(assetState)),
    );
}

export function stateHasWrittenDownValue(
  state: AssetState,
): state is PickNonNullable<AssetState, 'cost' | 'writtenDownValue'> {
  return state.cost != null && state.writtenDownValue != null;
}

export function transactionHasTaxableUse(
  data?: AssetTransactionDto,
): data is PickNonNullable<AssetTransactionDto, 'taxableUse'> {
  return !!data && data.taxableUse != null && data.taxableUse !== 1;
}

type AssetTransactionWithShowResidual = AssetTransactionDto & {
  showResidualValue?: boolean;
};

export function getTransactionDescription(
  locale: string,
  route: TypedActivatedRoute<RegisterParams, RegisterData>,
) {
  const percentPipe = new AaPercentPipe(locale);
  const currencyPipe = new AaCurrencyPipe(locale, route);
  const datePipe = new AaDatePipe(locale, route);

  return (data?: AssetTransactionWithShowResidual, assetGroups?: AssetGroupDto[]) => {
    if (!data) {
      return '';
    }

    const lines: string[] = [];

    switch (data.type) {
      case 'FirstUse':
      case 'ReversedFirstUse':
        formatAssessment(data, 'First Use', lines);
        break;

      case 'Reassessment':
      case 'ReversedReassessment':
        formatAssessment(data, 'Reassessment', lines);
        break;

      case 'PoolSale':
        return (
          `Sold from ${data.poolName} for ${currencyPipe.transform(data.price)}` +
          (transactionHasTaxableUse(data)
            ? ` (${percentPipe.transform(data.taxableUse)} Taxable Use)`
            : '')
        );
      case 'PoolWriteOff':
        return `Disposed from ${data.poolName}`;

      case 'TaxableUse':
      case 'ReversedTaxableUse':
      case 'PoolAssetTaxableUse':
      case 'ReversedPoolAssetTaxableUse':
        return `Taxable Use: ${percentPipe.transform(data.taxableUse)}`;

      case 'AddToPool':
      case 'TransferToPool':
      case 'ReversedTransferToPool':
        lines.push(
          `${data.type === 'AddToPool' ? 'Added' : 'Transferred'} to ${data.poolName}` +
            (transactionHasTaxableUse(data)
              ? ` (${currencyPipe.transform(data.transferredValue)} - ${percentPipe.transform(
                  data.taxableUse,
                )} Taxable Use)`
              : ''),
        );
        break;

      case 'FieldValueAssignedToAsset':
      case 'ReversedFieldValueAssignedToAsset':
        return `${data.fieldName} ${formatFromToFieldValues(data)}`;
      case 'FieldValueAssignedToAssetReversal':
        return `Reversal of ${data.fieldName} ${formatFromToFieldValues(data)}`;
      case 'FieldValueUpdatedForAsset':
        return `${data.fieldName} ${formatFromToFieldValues(data)}`;
      case 'ReversedFieldValueUpdatedForAsset':
        return `${data.fieldName} ${formatFromToFieldValues(data)}`;
      case 'FieldValueUpdatedForAssetReversal':
        return `Reversal of ${data.fieldName} ${formatFromToFieldValues(data)}`;

      case 'Revaluation': {
        const revaluationReserve =
          data.revaluationReserve && data.revaluationReserve > 0 ? data.revaluationReserve : 0;
        const revaluationLoss =
          data.revaluationReserve && data.revaluationReserve < 0 ? data.revaluationReserve : 0;
        lines.push(
          `Revaluation Reserve: ${currencyPipe.transform(revaluationReserve)}`,
          `Revaluation (Loss): ${currencyPipe.transform(revaluationLoss)}`,
        );
        break;
      }
      case 'Impairment':
        lines.push(
          `Impairment Loss or (Reversal): ${currencyPipe.transform(
            data.depreciation ? -data.depreciation : 0,
          )}`,
        );
        addIf(
          lines,
          data.impairmentProvision != null &&
            `Impairment Provision: ${currencyPipe.transform(data.impairmentProvision)}`,
        );
        break;

      case 'ComponentAdded':
      case 'ReversedComponentAdded':
        return data.componentName
          ? data.componentCode
            ? `Component added: ${data.componentCode} ${data.componentName}`
            : `Component added: ${data.componentName}`
          : `Component added: (Deleted)`;
      case 'ComponentAddedReversal':
        return `Reversal of component addition`;
      case 'ParentAdded':
      case 'ReversedParentAdded':
        return data.componentName
          ? data.componentCode
            ? `Parent added: ${data.parentCode} ${data.parentName}`
            : `Parent added: ${data.parentName}`
          : `Parent added: (Deleted)`;
      case 'ParentAddedReversal':
        return `Reversal of parent addition`;
      case 'ComponentRemoved':
      case 'ReversedComponentRemoved':
        return data.componentName
          ? data.componentCode
            ? `Component removed: ${data.componentCode} ${data.componentName}`
            : `Component removed: ${data.componentName}`
          : `Component removed: (Deleted)`;
      case 'ComponentRemovedReversal':
        return `Reversal of component removal`;
      case 'ParentRemoved':
      case 'ReversedParentRemoved':
        return data.componentName
          ? data.componentCode
            ? `Parent removed: ${data.parentCode} ${data.parentName}`
            : `Parent removed: ${data.parentName}`
          : `Parent removed: (Deleted)`;
      case 'ParentRemovedReversal':
        return `Reversal of parent removal`;

      case 'AssetGroupAssignedToAsset':
      case 'ReversedAssetGroupAssignedToAsset': {
        const assetGroup = assetGroups?.find(ag => ag.assetGroupId === data.assetGroupId);
        return `Group Assigned: ${assetGroup?.name ?? data.assetGroupId}`;
      }
      case 'AssetGroupAssignedToAssetReversal':
        return `Reversal of group assignment`;
    }

    addIf(lines, data.notes);

    return lines.join('\n');
  };

  function formatAssessment(data: AssetTransactionWithShowResidual, name: string, lines: string[]) {
    const details = [`${name}: ${data.methodCode}`];
    addIf(details, !!data.life && `/ ${data.life}` + (data.methodCode === 'UU' ? ' units' : 'yrs'));
    addIf(details, !!data.rate && `/ ${percentPipe.transform(data.rate)}`);
    addIf(details, data.isSelfAssessed && '(self-assessed)');
    lines.push(details.join(' '));

    addIf(
      lines,
      data.originalDate && `Original First Use: ${datePipe.transform(data.originalDate)}`,
    );
    addIf(
      lines,
      data.originalCost != null &&
        `Original Construction Cost: ${currencyPipe.transform(data.originalCost)}`,
    );

    addIf(
      lines,
      data.showResidualValue && `Residual Value: ${currencyPipe.transform(data.residualValue)}`,
    );
  }

  function formatFromToFieldValues(data: AssetTransactionDto) {
    let type = 'transferred';
    const parts: string[] = [];

    if (data.fromValues && data.fromValues.length > 0) {
      if (data.fromValues.length === 1 && data.fromValues[0].allocation === 1) {
        parts.push(`from ${data.fromValues[0].value}`);
      } else {
        parts.push(
          `from ${data.fromValues
            .map(x => `${x.value} (${(x.allocation * 100).toFixed(0)}%)`)
            .join(', ')}`,
        );
      }
    } else {
      type = 'assigned';
    }

    if (data.toValues && data.toValues.length > 0) {
      if (data.toValues.length === 1 && data.toValues[0].allocation === 1) {
        parts.push(`to ${data.toValues[0].value}`);
      } else {
        parts.push(
          `to ${data.toValues
            .map(x => `${x.value} (${(x.allocation * 100).toFixed(0)}%)`)
            .join(', ')}`,
        );
      }
    } else {
      type = 'removed';
    }

    parts.unshift(type);

    return parts.join(' ');
  }

  function addIf(lines: string[], line: string | null | undefined | false) {
    if (line) {
      lines.push(line);
    }
  }
}

export function getTransactionSource(
  data: AssetTransactionDto,
): [string, string | null | undefined, string | null | undefined] {
  if (data) {
    if (data.createdByUserFullName) {
      const created = data.created ? utcToLocalDate(data.created, 'YYYY-MM-DD HH:mm') : '';
      const source = data.createdBySource ? getIntegrationName(data.createdBySource) : undefined;
      const importName = data.importBySourceName;

      let viaText = '';

      if (source) {
        viaText = ` via ${source}`;
        if (data.externalReferenceNo) {
          let externalReferenceNo = data.externalReferenceNo;
          if (data.createdBySource === IntegrationType.QuickBooks) {
            externalReferenceNo = externalReferenceNo.split('-')[0];
          }

          if (externalReferenceNo) {
            viaText += ` (Ref: ${externalReferenceNo})`;
          }
        }
      } else if (importName) {
        viaText = ' via Import';
      } else if (data.sourceId) {
        viaText = ' via Bulk Action';
      }
      return [`${data.createdByUserFullName}: ${created}${viaText}`, importName, data.sourceId];
    }
  }
  return ['', undefined, undefined];
}

export function augmentTransactions(
  transactions: AssetTransactionDto[],
  {
    years,
    fyKeyCreator,
  }: { years: AccountingYear[]; fyKeyCreator: (params: { value: any }) => string },
): [
  allTransactions: AssetTransactionAdjusted[],
  unreversedTransactions: AssetTransactionAdjusted[],
] {
  const allTransactions: AssetTransactionAdjusted[] = [];
  const unreversedTransactions: AssetTransactionAdjusted[] = [];

  let lastResidualValue = 0;
  let lastUnrevaluedValue = 0;
  let hasLifeOrRate = false;
  transactions.forEach((txn, i) => {
    const fy = years.find(
      ({ endDate }) => toUniversalDateString(txn.date, { throwOnFailure: true }) <= endDate,
    );
    const financialYear = fy ? fyKeyCreator({ value: fy }) : null;
    const isUnreversed = ALL_TYPES_BUT_REVERSALS.includes(txn.type);

    const adjusted: AssetTransactionAdjusted = {
      ...txn,
      financialYear,
      index: i,
    };

    if (isUnreversed) {
      adjusted.residualValue = txn.residualValue ?? lastResidualValue;
      adjusted.showResidualValue = adjusted.residualValue !== lastResidualValue;
    }

    if (txn.type === 'OpeningBalance') {
      // Update all prior transactions
      allTransactions.forEach(prior => {
        prior.isPriorToOpeningBalance = true;
        prior.openingBalanceLabel = 'Estimated';

        if (prior.type === 'Depreciation') {
          prior.type = 'DepreciationEstimated';
        }
      });

      if (hasLifeOrRate && txn.writtenDownValue && txn.depreciation) {
        const previousWrittenDownValue = txn.writtenDownValue - txn.depreciation;
        // Percentage change from previous to current value: 1.0 = 100% increase, -0.5 = 50% decrease
        const change =
          previousWrittenDownValue === 0
            ? +Infinity
            : txn.writtenDownValue / previousWrittenDownValue - 1;

        if (Math.abs(change) >= 0.01) {
          const strChange =
            (Math.abs(change) >= 0.1 ? 'significantly ' : '') +
            (change > 0 ? 'increase' : 'reduce');

          (adjusted.warnings ??= []).push({
            text: [
              `The opening balance adjustment ${strChange}s the value of the asset.` +
                ` With straight-line methods this will ${strChange} the remaining life of the asset.`,
              'Please check depreciation after opening balance and reassess if necessary.',
            ],
            action: 'REASSESS',
          });
        }
      }
    }

    if (txn.type === 'Revaluation') {
      if (hasLifeOrRate && lastUnrevaluedValue === lastResidualValue) {
        adjusted.warnings = [
          {
            text: [
              'The revaluation occurs after the asset has been fully depreciated.',
              'It is recommended to reassess the asset after revaluation.',
            ],
            action: 'REASSESS',
          },
        ];
      }
    }

    if (txn.type === 'FirstUse' || txn.type === 'Reassessment') {
      // Not units & has life or rate
      hasLifeOrRate = txn.methodCode !== 'UU' && !!(txn.life || txn.rate);
    }

    if (txn.type === 'Reassessment') {
      unreversedTransactions.forEach(prior => {
        if (prior.warnings) {
          prior.warnings = prior.warnings.filter(w => w.action !== 'REASSESS');
          if (prior.warnings.length === 0) {
            delete prior.warnings;
          }
        }
      });
    }

    allTransactions.push(adjusted);
    if (isUnreversed) {
      unreversedTransactions.push(adjusted);

      lastResidualValue = txn.residualValue ?? lastResidualValue;
      lastUnrevaluedValue =
        adjusted.unrevaluedValue ?? adjusted.writtenDownValue ?? lastUnrevaluedValue;
    }
  });

  return [allTransactions, unreversedTransactions];
}
